/* =========================================================
   ESSE WERKS — VIN RESOLVER
   Two-stage decode used by /pages/configure-vin:
     Stage 1: WMI lookup (first 3 chars) → brand family
     Stage 2: chassis-pattern lookup (BMW family only) → chassis/model/engine

   Tables are based on ISO 3779 and reasonable industry knowledge.
   Not exhaustive (Phase 5 doesn't need NHTSA-level fidelity).
   Coverage is verified for the 6 seeded chassis (G20/F22/G42/G01/F56/G80)
   and includes a broad set of European BMW-adjacent brands for the
   "other brand" routing.

   Public API (window.ewVinResolver):
     validate(vin)        → boolean (17 alphanumeric, no I/O/Q)
     wmi(vin)             → first-3-char WMI string
     lookupWmi(vinOrWmi)  → { family: 'bmw'|'other', brand, country } | null
     lookupChassis(vin)   → { chassis, model, engine } | null  (BMW only)
     maskVin(vin)         → "WBA5R7C56MF******"
     resolve(vin)         → { kind, ... } — top-level decision tree
   ========================================================= */
(function () {
  'use strict';

  var VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;

  /* BMW Group — full configurator access. */
  var BMW_FAMILY_WMI = {
    'WBA': { brand: 'BMW',        country: 'Germany' },
    'WBS': { brand: 'BMW M',      country: 'Germany' },
    'WBY': { brand: 'BMW i',      country: 'Germany' },
    '4US': { brand: 'BMW',        country: 'USA (Spartanburg)' },
    '5UX': { brand: 'BMW',        country: 'USA (X3/X5)' },
    '5YM': { brand: 'BMW',        country: 'USA (X3/X5 M variants)' },
    'WMW': { brand: 'MINI',       country: 'UK/Germany' },
    'WMM': { brand: 'MINI',       country: 'UK (older plant)' },
    'SCA': { brand: 'Rolls-Royce',country: 'UK' },
    'WAP': { brand: 'Alpina',     country: 'Germany (legacy — newer Alpinas use BMW WMI)' }
  };

  /* Recognized non-BMW European brands — route to /configure/other-brand. */
  var RECOGNIZED_BRAND_WMI = {
    /* Mercedes-Benz family */
    'WDB': { brand: 'Mercedes-Benz', country: 'Germany' },
    'WDC': { brand: 'Mercedes-Benz', country: 'Germany (M-Class era)' },
    'WDD': { brand: 'Mercedes-Benz', country: 'Germany (passenger)' },
    'W1V': { brand: 'Mercedes-Benz', country: 'Germany (Sprinter / van)' },
    'W1K': { brand: 'Mercedes-Benz', country: 'Germany (newer passenger)' },
    'W1N': { brand: 'Mercedes-Benz', country: 'Germany (newer SUV)' },
    'W1Y': { brand: 'Mercedes-AMG',  country: 'Germany' },
    '4JG': { brand: 'Mercedes-Benz', country: 'USA (Tuscaloosa)' },
    '55S': { brand: 'Mercedes-Benz', country: 'USA' },
    'WMA': { brand: 'Maybach',       country: 'Germany (legacy)' },
    /* Porsche */
    'WP0': { brand: 'Porsche',       country: 'Germany (911/Cayman/Boxster)' },
    'WP1': { brand: 'Porsche',       country: 'Germany (Cayenne/Macan/Panamera/Taycan)' },
    /* Audi */
    'WAU': { brand: 'Audi',          country: 'Germany' },
    'WUA': { brand: 'Audi Sport',    country: 'Germany (RS models)' },
    '93U': { brand: 'Audi',          country: 'USA' },
    'TRU': { brand: 'Audi',          country: 'Hungary (TT etc.)' },
    /* Volkswagen */
    'WVW': { brand: 'Volkswagen',    country: 'Germany' },
    'WV1': { brand: 'Volkswagen',    country: 'Germany (van/commercial)' },
    '1VW': { brand: 'Volkswagen',    country: 'USA' },
    '3VW': { brand: 'Volkswagen',    country: 'Mexico' },
    /* Ferrari */
    'ZFF': { brand: 'Ferrari',       country: 'Italy' },
    /* Lamborghini */
    'ZHW': { brand: 'Lamborghini',   country: 'Italy' },
    'ZPB': { brand: 'Lamborghini',   country: 'Italy (newer)' },
    /* McLaren */
    'SBM': { brand: 'McLaren',       country: 'UK' },
    /* Aston Martin */
    'SCF': { brand: 'Aston Martin',  country: 'UK' },
    /* Lotus */
    'SCC': { brand: 'Lotus',         country: 'UK' },
    /* Maserati */
    'ZAM': { brand: 'Maserati',      country: 'Italy' },
    /* Bentley */
    'SCB': { brand: 'Bentley',       country: 'UK' },
    /* Alfa Romeo */
    'ZAR': { brand: 'Alfa Romeo',    country: 'Italy' },
    'ZAS': { brand: 'Alfa Romeo',    country: 'Italy (older)' },
    /* Bugatti */
    'VF9': { brand: 'Bugatti',       country: 'France' },
    /* Land Rover / Range Rover */
    'SAL': { brand: 'Land Rover',    country: 'UK' },
    /* Jaguar */
    'SAJ': { brand: 'Jaguar',        country: 'UK' },
    /* Volvo */
    'YV1': { brand: 'Volvo',         country: 'Sweden' },
    'YV4': { brand: 'Volvo',         country: 'USA' },
    /* Polestar */
    'LPS': { brand: 'Polestar',      country: 'China' }
  };

  /* Stage-2 chassis pattern lookup — BMW family only. */
  var BMW_CHASSIS_PATTERNS = [
    { pattern: /^WBA8E/,    chassis: 'F30', model: '330i',   engine: 'B48' },
    { pattern: /^WBA5R/,    chassis: 'G20', model: '330i',   engine: 'B48' },
    { pattern: /^WBA8J/,    chassis: 'F22', model: '230i',   engine: 'B48' },
    { pattern: /^WBA42/,    chassis: 'G42', model: '230i',   engine: 'B48' },
    { pattern: /^5UX4[3-9]/,chassis: 'G01', model: 'X3 30i', engine: 'B48' },
    { pattern: /^WMW[ML]/,  chassis: 'F56', model: 'JCW',    engine: 'B48' },
    { pattern: /^WBS43/,    chassis: 'G80', model: 'M3',     engine: 'S58' }
  ];

  /* Convenience: which makes are BMW-family WMIs (drives default 'make'
     value when a chassis pattern matches). Most BMW VINs → "BMW";
     WMW/WMM → "MINI"; SCA → "Rolls-Royce"; WAP → "Alpina". */
  function makeForWmi(w) {
    if (w === 'WMW' || w === 'WMM') return 'MINI';
    if (w === 'SCA') return 'Rolls-Royce';
    if (w === 'WAP') return 'Alpina';
    return 'BMW';
  }

  /* ---- Public API ---- */
  function validate(vin) {
    return VIN_REGEX.test(String(vin || '').toUpperCase());
  }

  function wmi(vin) {
    return String(vin || '').toUpperCase().slice(0, 3);
  }

  function lookupWmi(vinOrWmi) {
    var key;
    var s = String(vinOrWmi || '').toUpperCase();
    key = (s.length === 17) ? wmi(s) : s.slice(0, 3);
    if (BMW_FAMILY_WMI[key])       return Object.assign({ family: 'bmw',   wmi: key }, BMW_FAMILY_WMI[key]);
    if (RECOGNIZED_BRAND_WMI[key]) return Object.assign({ family: 'other', wmi: key }, RECOGNIZED_BRAND_WMI[key]);
    return null;
  }

  function lookupChassis(vin) {
    var u = String(vin || '').toUpperCase();
    for (var i = 0; i < BMW_CHASSIS_PATTERNS.length; i++) {
      var p = BMW_CHASSIS_PATTERNS[i];
      if (p.pattern.test(u)) return { chassis: p.chassis, model: p.model, engine: p.engine };
    }
    return null;
  }

  function maskVin(vin) {
    if (!vin || vin.length < 7) return vin || '';
    return vin.slice(0, vin.length - 6) + '******';
  }

  function resolve(vin) {
    var v = String(vin || '').toUpperCase();
    if (!validate(v)) return { kind: 'invalid', vin: v };
    var w = lookupWmi(v);
    if (!w) return { kind: 'unknown', vin: v, wmi: wmi(v) };
    if (w.family === 'other') {
      return { kind: 'other_brand', vin: v, brand: w.brand, country: w.country, wmi: w.wmi };
    }
    /* BMW family */
    var c = lookupChassis(v);
    if (!c) {
      return { kind: 'bmw_unknown', vin: v, brand: w.brand, country: w.country, wmi: w.wmi };
    }
    return {
      kind:        'bmw_chassis',
      vin:         v,
      brand:       w.brand,
      country:     w.country,
      wmi:         w.wmi,
      chassis:     c.chassis,
      model:       c.model,
      engine:      c.engine,
      make:        makeForWmi(w.wmi)
    };
  }

  window.ewVinResolver = {
    validate:       validate,
    wmi:            wmi,
    lookupWmi:      lookupWmi,
    lookupChassis:  lookupChassis,
    maskVin:        maskVin,
    resolve:        resolve,
    _tables: {
      BMW_FAMILY_WMI:        BMW_FAMILY_WMI,
      RECOGNIZED_BRAND_WMI:  RECOGNIZED_BRAND_WMI,
      BMW_CHASSIS_PATTERNS:  BMW_CHASSIS_PATTERNS
    }
  };
})();
